/*
 * Scroll reveal: headings, text, images, videos, buttons and cards fade in with a slight
 * rise as they scroll into view (styles in umikaze-custom.css). Loaded from layout/theme.liquid when the theme setting
 * "umikaze_scroll_reveal" is on (never in the theme editor).
 *
 * - Skips the first section of each page (hero / product info) and anything outside
 *   #MainContent (header, footer, drawers).
 * - Elements already in the viewport at start-up are left alone, so nothing that is
 *   visible on first paint disappears and flashes back in.
 * - Cards and in-section sliders reveal as one unit; their contents are not split up.
 * - Blocks that run their own motion (about story, parallax, slideshows, card stack)
 *   are skipped.
 * - Each element reveals once. Afterwards every class and style added here is removed,
 *   so the element's own transitions, filters and stacking return to normal.
 */
(() => {
  if (window.umikazeScrollReveal) return;
  window.umikazeScrollReveal = true;

  if (!('IntersectionObserver' in window)) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  /* Revealed as a whole; nothing inside them is revealed separately */
  const UNIT_SELECTOR = [
    'product-card',
    '.collection-card',
    '.resource-card',
    '.blog-post-card',
    'slideshow-component',
    'marquee-component',
  ].join(',');

  /* Individual pieces of content */
  const LEAF_SELECTOR = [
    '.text-block',
    '.ai-bilingual-heading',
    '.image-block',
    'deferred-media.size-style',
    'a.size-style:is(.button, .button-secondary, .link)',
  ].join(',');

  /*
   * Images moved by assets/umikaze-about-parallax.js. Keep in sync with the selector in
   * that file. Matched against the markup itself rather than the classes that script
   * adds, so the result does not depend on which script runs first. Only applies on
   * pages that load the parallax script.
   */
  const PARALLAX_IMAGE_SELECTOR =
    'main .image-block img, main [id$="__care_section"] .media-block img, main [id$="__people_section"] .media-block img';
  const hasParallax = Boolean(document.querySelector('script[src*="umikaze-about-parallax"]'));

  /* Regions that manage their own motion or must never be hidden */
  const EXCLUDE_SELECTOR = [
    'header',
    'footer',
    'dialog',
    '.product-information',
    '[data-parallax-layer]',
    '.umikaze-parallax-frame',
    '.umikaze-parallax-image',
  ].join(',');

  /* Custom elements of blocks with their own animation (tag names carry a per-block id) */
  const EXCLUDE_TAG_PREFIXES = ['UMIKAZE-ABOUT-', 'AI-ABOUT-SLIDESHOW-', 'CARD-STACK-'];

  const MAX_STAGGER_STEPS = 5;
  const STAGGER_MS = 90;
  const CLEANUP_FALLBACK_MS = 2000;

  /** @param {Element} element */
  const isInViewport = (element) => {
    const rect = element.getBoundingClientRect();
    return rect.bottom > 0 && rect.top < window.innerHeight && rect.width > 0 && rect.height > 0;
  };

  /** @param {HTMLElement} element */
  const finish = (element) => {
    element.classList.remove('umikaze-reveal', 'umikaze-reveal--in');
    element.style.removeProperty('--umikaze-reveal-delay');
  };

  /** @param {HTMLElement} element @param {number} delay */
  const reveal = (element, delay) => {
    element.style.setProperty('--umikaze-reveal-delay', `${delay}ms`);
    element.classList.add('umikaze-reveal--in');

    const done = () => {
      clearTimeout(fallback);
      element.removeEventListener('transitionend', onEnd);
      finish(element);
    };

    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target === element && event.propertyName === 'opacity') done();
    };

    const fallback = setTimeout(done, CLEANUP_FALLBACK_MS + delay);
    element.addEventListener('transitionend', onEnd);
  };

  const init = () => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    const main = document.getElementById('MainContent');
    if (!main) return;

    const sections = [...main.children].filter((child) => child.classList.contains('shopify-section'));
    const candidateSelector = `${UNIT_SELECTOR},${LEAF_SELECTOR}`;
    // Share ancestor checks across siblings instead of repeating closest() walks.
    const excluded = new WeakMap();
    const nested = new WeakMap();
    const parallaxContainers = new Set();
    if (hasParallax) {
      for (const image of main.querySelectorAll(PARALLAX_IMAGE_SELECTOR)) {
        for (let node = image; node && !parallaxContainers.has(node); node = node.parentElement) {
          parallaxContainers.add(node);
        }
      }
    }
    const isExcluded = (element) => {
      if (!element) return false;
      if (!excluded.has(element)) {
        excluded.set(element, element.matches(EXCLUDE_SELECTOR) ||
          EXCLUDE_TAG_PREFIXES.some((prefix) => element.tagName.startsWith(prefix)) ||
          isExcluded(element.parentElement));
      }
      return excluded.get(element);
    };
    const isNested = (element) => {
      if (!element) return false;
      if (!nested.has(element)) {
        nested.set(element, element.matches(candidateSelector) || isNested(element.parentElement));
      }
      return nested.get(element);
    };

    /** @type {HTMLElement[]} */
    const targets = [];

    for (const section of sections.slice(1)) {
      for (const element of section.querySelectorAll(candidateSelector)) {
        if (!(element instanceof HTMLElement)) continue;
        if (isNested(element.parentElement) || isExcluded(element) || parallaxContainers.has(element)) continue;
        // Measure at actual initialization time, before any hidden classes are added.
        if (isInViewport(element) || element.contains(document.activeElement)) continue;

        targets.push(element);
      }
    }

    if (targets.length === 0) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entering = entries
          .filter((entry) => entry.isIntersecting)
          .map((entry) => /** @type {HTMLElement} */ (entry.target))
          .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1));

        entering.forEach((element, index) => {
          observer.unobserve(element);
          reveal(element, Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS);
        });
      },
      { rootMargin: '0px 0px -8% 0px' }
    );

    for (const element of targets) {
      element.classList.add('umikaze-reveal');
      observer.observe(element);
    }
  };

  const schedule = () => {
    // Yield out of DOMContentLoaded; content stays visible until enhancement runs.
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(init, { timeout: 1000 });
    } else {
      window.setTimeout(init, 0);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', schedule, { once: true });
  } else {
    schedule();
  }
})();
